<template>
    <div class="qanda-widget">
        <div class="qanda-header"> 
           <span class="widget-subtitle">{{ t('widgets.qna.description') }}</span>
        </div>

        <!-- Submit Question Form -->
        <div class="question-form">
            <div class="form-group">
                <div class="textarea-wrapper">
                    <textarea
                        v-model="questionText"
                        :placeholder="t('widgets.qna.placeholder')"
                        rows="3"
                        maxlength="500"
                        class="form-control"
                        :disabled="isSubmitting"
                    ></textarea>
                    <span class="char-count">
                        {{ questionText.length }}/500
                    </span>
                </div>
                <div class="form-footer">
                    <label class="anonymous-check">
                        <input type="checkbox" v-model="sendAnonymous" :disabled="!isLoggedIn" />
                        <span>{{ t('widgets.qna.anonymous') }}</span>
                    </label>
                    <button
                        @click="submitQuestion"
                        :disabled="!canSubmit"
                        class="btn btn-submit"
                    >
                        {{ isSubmitting ? t('widgets.qna.submitting') : t('widgets.qna.askQuestion') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Questions List -->
        <div v-if="questions.length > 0" class="questions-list">
            <h4 class="questions-title">{{ t('widgets.qna.recentQuestions') }}</h4>
            <div class="questions-container">
                <div 
                    v-for="question in displayedQuestions" 
                    :key="question.id"
                    class="question-item"
                >
                    <!-- Upvote button in top-right corner -->
                    <button
                        @click="upvoteQuestion(question.id)"
                        :disabled="hasUpvoted(question.id) || isUpvoting"
                        class="upvote-badge"
                        :class="{ 'upvoted': hasUpvoted(question.id) }"
                        :title="hasUpvoted(question.id) ? t('widgets.qna.upvoted') : t('widgets.qna.upvote')"
                    >
                        <svg class="upvote-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                        </svg>
                        <span class="vote-count">{{ question.votes }}</span>
                    </button>
                    
                    <div class="question-content">
                        <p class="question-text">{{ question.text }}</p>
                        <div class="question-meta">
                            <span class="question-author" v-if="question.user_name">{{ question.user_name }} · </span>
                            <span class="question-date">{{ formatDate(question.created_at) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="status-message">
            <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="status-title">Noch keine Fragen. Stell als Erste/r deine Frage!</p>
        </div>

        <div v-if="error" class="alert alert-danger">
            {{ error }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/useApi.js'
import { useWebSocket } from '@/composables/useWebSocket.js'
import { useAudienceConfig } from '@/composables/useAudienceConfig.js'
import { validateConfig } from '@/utils/config.js'

const { t } = useI18n()

const props = defineProps({
    qanda: {
        type: Object,
        required: true
    },
    sessionId: {
        type: String,
        required: true
    },
    // External configuration props
    apiBaseUrl: {
        type: String,
        default: ''
    },
    pusherConfig: {
        type: Object,
        default: () => ({})
    },
    authToken: {
        type: String,
        default: null
    },
    congressId: {
        type: [String, Number],
        default: null
    },
    forumName: {
        type: String,
        default: 'Anonymous'
    }
})

const emit = defineEmits(['question-submitted', 'session-stopped', 'session-started'])

// Configuration setup - use the new composable for consistent config
const config = useAudienceConfig(props)

// Validate configuration
const configValidation = computed(() => validateConfig(config.value))

// Setup API client
const { api } = useApi(config.value)

// Setup WebSocket
const { createEcho, subscribeToChannel } = useWebSocket(config.value)

// Reactive state
const questionText = ref('')
const questions = ref([])
const qandaItem = ref({})
const isSubmitting = ref(false)
const isUpvoting = ref(false)
const error = ref('')
const upvotedQuestions = ref(new Set())
const sendAnonymous = ref(true)

// Real-time updates
let channel = null
let echo = null

// Computed
const isLoggedIn = computed(() => {
    return props.forumName && props.forumName !== 'Anonymous' && props.forumName !== 'anonymous'
})
const canSubmit = computed(() => {
    return questionText.value.trim().length > 0 && questionText.value.trim().length <= 500 && !isSubmitting.value
})

const displayedQuestions = computed(() => {
    return questions.value
        .filter(q => !q.private) // Only show public questions to audience
        .sort((a, b) => b.votes - a.votes || new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10) // Limit to 10 most recent/popular questions
})

// Methods
const submitQuestion = async () => {
    if (!canSubmit.value) return

    // Check if we have the qandaItem loaded with an ID
    if (!qandaItem.value || !qandaItem.value.id) {
        error.value = 'Q&A session not loaded. Please wait a moment and try again.'
        return
    }

    isSubmitting.value = true
    error.value = ''

    try {
        const response = await api.post(`/api/audience/qanda/${qandaItem.value.id}/questions`, {
            question: questionText.value.trim(),
            user_name: sendAnonymous.value ? null : props.forumName
        })

        // Don't add the question locally - let it come through the broadcast
        // This prevents duplicates and ensures all clients see questions in the same order
        questionText.value = ''

        emit('question-submitted', {
            qanda_id: qandaItem.value.id,
            question: response.data
        })

    } catch (err) {
        console.error('Error submitting question:', err)
        
        if (err.response?.status === 422) {
            // Validation errors
            const errors = err.response.data.errors || {}
            if (errors.question && errors.question[0]) {
                error.value = errors.question[0]
            } else {
                error.value = err.response.data.message || 'Please check your input.'
            }
        } else if (err.response?.status === 409) {
            error.value = 'You have already submitted this question.'
        } else if (err.response?.status === 404) {
            error.value = t('widgets.qna.sessionNotActive')
        } else {
            error.value = 'Failed to submit question. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}

const upvoteQuestion = async (questionId) => {
    if (hasUpvoted(questionId) || isUpvoting.value) return

    isUpvoting.value = true

    try {
        const response = await api.post(`/api/audience/questions/${questionId}/upvote`)

        // Update the question in the list
        const questionIndex = questions.value.findIndex(q => q.id === questionId)
        if (questionIndex !== -1) {
            questions.value[questionIndex].votes = response.data.votes
        }

        // Mark as upvoted
        upvotedQuestions.value.add(questionId)

    } catch (err) {
        console.error('Error upvoting question:', err)
        
        if (err.response?.status === 409) {
            // User already upvoted, mark it locally
            upvotedQuestions.value.add(questionId)
        }
    } finally {
        isUpvoting.value = false
    }
}

const hasUpvoted = (questionId) => {
    return upvotedQuestions.value.has(questionId)
}

const loadQuestions = async () => {
    // Wait for qandaItem to be loaded first
    if (!qandaItem.value || !qandaItem.value.id) {
        console.log("⏳ Q&A item not loaded yet, waiting...", qandaItem)
        return
    }

    try {
        console.log("🔍 Loading questions with API baseURL:", config.value.apiBaseUrl)
        // Use the configured API client instead of direct axios
        const response = await api.get(`/api/audience/qanda/${qandaItem.value.id}/questions`)
        questions.value = response.data.questions || []
        
        console.log("✅ Questions loaded successfully:", questions.value.length)
        
        // Load upvoted questions from localStorage or API
        const upvoted = localStorage.getItem(`qanda_${qandaItem.value.id}_upvoted`)
        if (upvoted) {
            upvotedQuestions.value = new Set(JSON.parse(upvoted))
        }
    } catch (err) {
        console.error('❌ Error loading questions:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/audience/qanda/${props.qanda.id}/questions`
        })
    }
}

const loadQandaItem = async () => {
    try {
        console.log("🔍 Loading Q&A item with API baseURL:", config.value.apiBaseUrl)
        // Use the same prefix as other routes in the component (/api/audience/)
        const response = await api.get(`/api/sessions/${props.sessionId}/qanda`)
        qandaItem.value = response.data || {}  // Extract the data from the response
        
        console.log("✅ Q&A item loaded successfully:" , qandaItem.value)
        console.log("🔍 Q&A item ID:", qandaItem.value.id)
        
        // Once qandaItem is loaded, load the questions
        if (qandaItem.value && qandaItem.value.id) {
            console.log("🚀 Q&A item has ID, loading questions...")
            await loadQuestions()
            
            // Check if the Q&A session is already active and emit event to show widget
            if (qandaItem.value.active) {
                console.log("✅ Q&A session is already active, emitting session-started event")
                emit('session-started', { qanda: qandaItem.value })
            } else {
                console.log("ℹ️ Q&A session exists but is not active yet")
            }
        } else {
            console.warn("⚠️ Q&A item loaded but no ID found:", qandaItem.value)
        }
        
    } catch (err) {
        console.error('❌ Error loading Q&A item:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/sessions/${props.sessionId}/qanda`
        })
    }
}

const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    
    if (diffMins < 1) return t('timeFormat.justNow')
    if (diffMins < 60) return `${diffMins}${t('timeFormat.minutesAgo')}`
    if (diffHours < 24) return `${diffHours}${t('timeFormat.hoursAgo')}`
    return date.toLocaleDateString()
}

// Save upvoted questions to localStorage
const saveUpvotedQuestions = () => {
    if (qandaItem.value && qandaItem.value.id) {
        localStorage.setItem(
            `qanda_${qandaItem.value.id}_upvoted`, 
            JSON.stringify([...upvotedQuestions.value])
        )
    }
}

// Watch for changes in upvoted questions
watch(upvotedQuestions, saveUpvotedQuestions, { deep: true })

// Initialize real-time updates using useWebSocket composable
const initializePusher = () => {
    console.log("🚀 INITIALIZE Q&A PUSHER - START")
    console.log("📋 Current config:", config.value)
    console.log("🌍 Global AUDIENCE_CONFIG:", window.AUDIENCE_CONFIG)
    
  try {
    // Use the imported composable
    console.log("📡 Creating Echo instance with config:", config.value)
    echo = createEcho()
    
    if (!echo) {
      console.warn('❌ Failed to create Echo instance, real-time updates disabled')
      return
    }
    
    console.log("✅ Echo instance created successfully")

    // Subscribe to the session channel with improved congress_id handling
    let finalCongressId = props.congressId

    finalCongressId = parseInt(finalCongressId)
    if (isNaN(finalCongressId)) {
      console.error('QandAWidget: Invalid congressId format, using fallback 1657')
      finalCongressId = 1657
    }
    
    const channelName = `session.${finalCongressId}.${props.sessionId}`
    console.log('QandA Widget subscribing to channel:', channelName)
    console.log('QandA Widget ✅ Using congress_id:', finalCongressId)
    
    // Use the subscribeToChannel composable
    channel = subscribeToChannel(echo, channelName, {
      'qanda.started': (data) => {
        console.log('Q&A session started:', data)
        error.value = ''
        if (data.qanda && data.qanda.active) {
            console.log("Q&A session started, emitting event to parent")
            console.log(data)
            loadQuestions()
            // Emit event to parent component
            emit('session-started', { qanda: data.qanda })
        }
      },
      
      'qanda.stopped': (data) => {
        console.log('Q&A session stopped:', data)
        questions.value = []
        error.value = t('widgets.qna.sessionEnded')
        // Emit event to parent component to hide the widget
        emit('session-stopped', { qanda: data.qanda })
      },
      
      'qanda.private_mode': (data) => {
        console.log('Q&A switched to private mode:', data)
        if (data.publicQuestions) {
          questions.value = questions.value.filter(q => data.publicQuestions.some(pq => pq.id === q.id))
        }
      },
      
      'qanda.public_mode': (data) => {
        console.log('Q&A switched to public mode:', data)
        if (data.publicQuestions) {
          questions.value = data.publicQuestions
        } else {
          questions.value = []
        }
      },
      
      'question.submitted': (data) => {
        console.log('New question received:', data)
       // data.user_name
        if (data.question && !data.question.private) {
          if (!questions.value.find(q => q.id === data.question.id)) {
            questions.value.unshift(data.question)
          }
        }
      },
      
      'question.upvoted': (data) => {
        console.log('Question upvoted:', data)
        if (data.question && !data.question.private) {
          const questionIndex = questions.value.findIndex(q => q.id === data.question.id)
          if (questionIndex !== -1) {
            questions.value[questionIndex].votes = data.question.votes
          }
        }
      },
      
      'question.privacy_toggled': (data) => {
        console.log('Question privacy toggled:', data)
        if (data.question) {
          const questionIndex = questions.value.findIndex(q => q.id === data.question.id)
          
          if (data.question.private) {
            if (questionIndex !== -1) {
              questions.value.splice(questionIndex, 1)
            }
          } else {
            if (questionIndex === -1) {
              questions.value.unshift(data.question)
            } else {
              questions.value[questionIndex] = data.question
            }
          }
        }
      }
    })
    
  } catch (error) {
    console.error('Error initializing WebSocket for QandA Widget:', error)
  }
}

const cleanupPusher = () => {
  if (channel) {
    try {
      channel.stopListening()
    } catch (error) {
      console.log('Channel cleanup handled by composable')
    }
  }
  // The useWebSocket composable handles Echo cleanup internally
  // Don't disconnect Echo if it's the global instance
  if (echo && echo !== window.Echo) {
    echo.disconnect()
  }
}

// Lifecycle
onMounted(() => {
    if (props.sessionId) {
        console.log('QandA Widget mounted, loading Q&A item...')
        console.log('Session ID:', props.sessionId)
       
        loadQandaItem() // This will load questions after qandaItem is loaded
        initializePusher()
    } else {
        console.warn('QandA Widget mounted without valid sessionId, skipping initialization.')
    }
})

onUnmounted(() => {
    cleanupPusher()
})
</script>

<style scoped>

.btn-submit{
    background: #7b6a58; 
    color: white
}
/* Q&A Widget Bootstrap Styling */
.qanda-widget {
    background: linear-gradient(135deg, hwb(46 82% 8%) 0%, #f5e5e5 100%);
    padding: 0.5rem;
    margin-bottom: 0.25rem;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.12);
    animation: slideIn 0.3s ease-out;
}

.qanda-header {
    margin-bottom: 0.375rem;
    text-align: center;
}

.widget-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2e7d32;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
}

.widget-subtitle {
    color: #424242;
    font-size: 0.75rem;
    font-weight: 400;
}

/* Question Form */
.question-form {
    margin-bottom: 0.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.textarea-wrapper {
    position: relative;
}

.form-control {
    width: 100%;
    resize: none;
    border: 1px solid #7b6a58;
    border-radius: 0.375rem;
    padding: 0.375rem;
    padding-bottom: 1.25rem;
    font-size: 0.8125rem;
    transition: border-color 0.2s ease;
}

.form-control:focus {
    border-color: #7b6a58;
    box-shadow: 0 0 0 0.15rem rgba(76, 175, 80, 0.2);
    outline: none;
}

.char-count {
    position: absolute;
    bottom: 0.25rem;
    right: 0.375rem;
    font-size: 0.6875rem;
    color: #6c757d;
    background: rgba(255, 255, 255, 0.9);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    pointer-events: none;
}

.form-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
}

.anonymous-check {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: #555;
    cursor: pointer;
    user-select: none;
}

.anonymous-check input[type="checkbox"] {
    accent-color: #7b6a58;
    cursor: pointer;
}

.anonymous-check input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.question-author {
    font-weight: 600;
    color: #7b6a58;
}

/* Questions List */
.questions-list {
    margin-top: 0.375rem;
}

.questions-title {
    font-weight: 600;
    color: #424242;
    margin-bottom: 0.375rem;
    font-size: 0.8125rem;
}

.questions-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-height: 15rem;
    overflow-y: auto;
}

.question-item {
    background: #ffffff;
    border: 1px solid #c8e6c9;
    border-radius: 0.25rem;
    padding: 0.375rem;
    padding-top: 0.25rem;
    position: relative;
    animation: fadeIn 0.5s ease-out;
}

.question-content {
    padding-right: 2.5rem;
}

.question-text {
    color: #424242;
    font-weight: 500;
    margin-bottom: 0.125rem;
    font-size: 0.8125rem;
    line-height: 1.3;
}

.question-meta {
    display: flex;
    align-items: center;
    font-size: 0.625rem;
    color: #6c757d;
}

.question-date {
    color: #6c757d;
}

/* Upvote Badge in Top-Right Corner */
.upvote-badge {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.0625rem;
    padding: 0.1875rem 0.25rem;
    min-width: 1.875rem;
    border: 1px solid #81c784;
    border-radius: 0.375rem;
    background: #ffffff;
    color: #7b6a58;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.upvote-badge:hover:not(:disabled) {
    background: #e8f5e8;
    border-color: #7b6a58;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
}

.upvote-badge:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.upvote-badge.upvoted {
    background: #e8f5e8;
    color: #2e7d32;
    border-color: #4caf50;
    font-weight: 600;
}

.upvote-badge .upvote-icon {
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
}

.vote-count {
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1;
}

/* Status Message */
.status-message {
    text-align: center;
    padding: 0.75rem;
    color: #6c757d;
}

.status-icon {
    width: 1.5rem;
    height: 1.5rem;
    margin: 0 auto 0.375rem;
    color: #6c757d;
}

.status-title {
    font-weight: 500;
    color: #424242;
    margin: 0;
    font-size: 0.8125rem;
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from { 
        opacity: 0;
        transform: scale(0.95);
    }
    to { 
        opacity: 1;
        transform: scale(1);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .qanda-widget {
        padding: 0.5rem;
    }
    
    .question-content {
        flex-direction: column;
        align-items: stretch;
        gap: 0.375rem;
    }
    
    .upvote-btn {
        align-self: flex-start;
    }
    
    .form-footer {
        flex-direction: column;
        align-items: stretch;
        text-align: center;
    }
    
    .question-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }
}
</style>